import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ChevronRight, Globe, LogOut, Trash2, QrCode, User, ShoppingBag, Settings } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const ProfilePage = () => {
    const navigate = useNavigate();
    const { user, logout, api } = useAuth();
    const { language, setLanguage, t } = useLanguage();
    const [showQR, setShowQR] = useState(false);
    const [attendance, setAttendance] = useState(null);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [tickets, setTickets] = useState([]);

    // Scroll naar top bij laden - fix voor PWA mode
    useEffect(() => {
        // Force scroll to top immediately and after a short delay
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        
        // Also try after render
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
        }, 100);
        
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        fetchAttendance();
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            const ticketsRes = await api().get('/tickets/my');
            setTickets(ticketsRes.data || []);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
        }
    };

    const fetchAttendance = async () => {
        try {
            const response = await api().get('/users/me/attendance');
            setAttendance(response.data.attendance);
            setCurrentEvent(response.data.event);
        } catch (error) {
            console.error('Failed to fetch attendance:', error);
        }
    };

    const handleLogout = async () => {
        await logout();
        sessionStorage.removeItem('galeries_invite_code');
        navigate('/');
    };

    const handleDeleteAccount = async () => {
        try {
            await api().delete('/auth/account');
            await logout();
            sessionStorage.removeItem('galeries_invite_code');
            toast.success('Account deleted');
            navigate('/');
        } catch (error) {
            toast.error('Failed to delete account');
        }
    };

    // Generate a simple QR code data URL (placeholder - in production use a QR library)
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`GALERIES_YESS_USER:${user?.id}`)}`;

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-center py-4">
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                </div>
            </div>

            {/* Content with extra padding for PWA safe area */}
            <div className="pt-28 px-6">
                <h1 className="text-2xl font-heading font-light tracking-tight mb-8">
                    {t('profile')}
                </h1>

                {/* User Info */}
                <div className="mb-8">
                    <div className="flex items-center gap-4 mb-6">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                            <span className="text-2xl font-heading">
                                {user?.name?.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div>
                            <h2 className="text-xl font-heading">{user?.name}</h2>
                            <p className="text-sm text-gray-500">{user?.phone}</p>
                        </div>
                    </div>
                </div>

                {/* Swap Credits - Always visible */}
                <div className="mb-8 p-5 bg-[#FCE7F0] border border-[#F8BBD6] rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <ShoppingBag className="w-5 h-5 text-[#F8BBD6]" />
                            <span className="text-sm font-medium">{t('swapCredits')}</span>
                        </div>
                        {currentEvent && (
                            <span className="text-xs text-gray-400">{currentEvent.title}</span>
                        )}
                    </div>
                    {attendance?.checked_in_at ? (
                        <div className="space-y-3">
                            {/* Progress bar style display */}
                            <div className="flex items-center gap-4">
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <span className="text-3xl font-heading font-semibold">{attendance.swap_credits || 0}</span>
                                        <span className="text-sm text-gray-400">/ 25 max</span>
                                    </div>
                                    <div className="h-2 bg-white rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-[#F8BBD6] rounded-full transition-all duration-300"
                                            style={{ width: `${Math.min(((attendance.swap_credits || 0) / 25) * 100, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <p className="text-xs text-gray-500">
                                {t('swapCreditsUse')}
                            </p>
                        </div>
                    ) : (
                        <div className="text-center py-2">
                            <p className="text-gray-500 mb-1">{t('notCheckedIn')}</p>
                            <p className="text-xs text-gray-400">
                                {t('swapCreditsInfo')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Menu Items */}
                <div className="space-y-1">
                    {/* QR Code */}
                    <button
                        onClick={() => setShowQR(!showQR)}
                        className="w-full flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        data-testid="profile-qr"
                    >
                        <div className="flex items-center gap-3">
                            <QrCode className="w-5 h-5 text-gray-400" />
                            <span className="text-sm tracking-wide">{t('personalQR')}</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300" />
                    </button>

                    {showQR && (
                        <div className="py-6 flex justify-center border-b border-gray-100 bg-gray-50">
                            <img 
                                src={qrCodeUrl} 
                                alt="Personal QR Code" 
                                className="w-48 h-48"
                                data-testid="qr-code-image"
                            />
                        </div>
                    )}

                    {/* Language */}
                    <div className="w-full flex items-center justify-between py-4 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                            <Globe className="w-5 h-5 text-gray-400" />
                            <span className="text-sm tracking-wide">{t('language')}</span>
                        </div>
                        <Select 
                            value={language} 
                            onValueChange={(val) => {
                                setLanguage(val);
                                toast.success(val === 'en' ? 'Language set to English' : 'Taal ingesteld op Nederlands');
                            }}
                        >
                            <SelectTrigger className="w-28 border-gray-200 bg-white text-right" data-testid="language-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white z-[100]">
                                <SelectItem value="en">English</SelectItem>
                                <SelectItem value="nl">Nederlands</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Personal Details */}
                    <button
                        onClick={() => navigate('/profile/details')}
                        className="w-full flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        data-testid="profile-details"
                    >
                        <div className="flex items-center gap-3">
                            <User className="w-5 h-5 text-gray-400" />
                            <span className="text-sm tracking-wide">{t('personalDetails')}</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300" />
                    </button>

                    {/* Admin Panel - Only for admins */}
                    {user?.is_admin && (
                        <button
                            onClick={() => navigate('/admin')}
                            className="w-full flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                            data-testid="profile-admin"
                        >
                            <div className="flex items-center gap-3">
                                <Settings className="w-5 h-5 text-[#F8BBD6]" />
                                <span className="text-sm tracking-wide font-medium">{t('adminPanel')}</span>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-300" />
                        </button>
                    )}

                    {/* Logout */}
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        data-testid="profile-logout"
                    >
                        <div className="flex items-center gap-3">
                            <LogOut className="w-5 h-5 text-gray-400" />
                            <span className="text-sm tracking-wide">{t('logOut')}</span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300" />
                    </button>

                    {/* Delete Account */}
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button
                                className="w-full flex items-center justify-between py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                data-testid="profile-delete"
                            >
                                <div className="flex items-center gap-3">
                                    <Trash2 className="w-5 h-5 text-red-400" />
                                    <span className="text-sm tracking-wide text-red-500">{t('deleteAccount')}</span>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-300" />
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-white">
                            <AlertDialogHeader>
                                <AlertDialogTitle>{t('deleteAccount')}</AlertDialogTitle>
                                <AlertDialogDescription>
                                    {t('deleteAccountConfirm')}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleDeleteAccount}
                                    className="bg-red-500 hover:bg-red-600"
                                >
                                    {t('delete')}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
